import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Prompt,
  PromptFormMode,
  SentenceSectionInfo,
  SentenceTemplateInfo,
} from "../types";
import {
  Button,
  Divider,
  Form,
  Icon,
  Modal,
  Popup,
  Ref,
  TextArea,
  TextAreaProps,
} from "semantic-ui-react";
import { useGetCustomerQuery } from "../../../api/customerApi";
import {
  buildSentence,
  findLanguage,
  getCustomerEnglishLanguage,
  getSectionContext,
  preventOverflowOnTextarea,
  updateSentenceValueFactory,
  withGenerationLog,
} from "../utils";
import { Text } from "../../../components/Text";
import { TemplateLabelChip } from "../../../vocabulary/templatesTabLabelSelector/TemplateLabelChip";
import {
  AddSentenceToProductTextBlockResponse,
  createProductSpecificOverwrite,
  GenerateOneResponse,
  getModels,
  GptGenerationTaskQueue,
  updatePrompt,
} from "../../../api/gptApi";
import { ProductId } from "../../product";
import { useSelector } from "react-redux";
import { RootState, store } from "../../../utils/store";
import { PromptSuggestionsComponent } from "../PromptSuggestionsComponent";
import { PromptForm } from "../PromptForm";
import {
  CopyAssistantContext,
  CopyAssistantDispatchContext,
} from "../CopyAssistantContext/CopyAssistantContext";
import { CopyAssistantActionType } from "../CopyAssistantContext/types";

import { selectedPromptTestIds } from "../testUtils/testIdsSelectors";
import { FieldsetSelection } from "./FieldsetSelection";
import { debounce } from "../../../utils/debounce";
import { ImageUrlInput } from "./ImageUrlInput";
import {
  useGetDocumentSections,
  useGetDocumentStructures,
} from "../../../planner/document-structure/manage/customhooks";
import { reloadPreview } from "../../../legacy/t.product-detail";
import { FlexDivTwoItems } from "./SelectedPromptGroupPromptItem";
import {
  GenerationDisclaimer,
  GroupedGenerationButtons,
  RevertButton,
} from "./Components/Buttons";
import { RunPipeline } from "../../actions/RunPipeline";
import { Button as TailwindButton } from "../../../components/tailwind/button/Button";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../../../api/djangoToastSlice";
import { LangStringObject } from "../../../vocabulary/LangString";
import { useSelectedText } from "../../../utils/reactCustomHooks";

export const explainInstruction = {
  header: "The instruction tells the model how to generate text",
  content:
    "Example: Write a category page text for fashion with focus on getting the best possible SEO. Repeat the category 10-14 times in the text. The text should be around 300 words.",
};
export const explainInputText = {
  header: "The prompt data tells the model what to write about",
  content:
    "Mostly these are fetched from the product data but you can change it here to tailor to your generation needs.",
};

export const exampleInstruction = "Write a product description";

type Props = {
  productId: ProductId;
  gptGenerationTaskQueue: GptGenerationTaskQueue;
  refreshPrompts: () => Promise<void>;
};
export const SelectedPrompt: React.FC<Props> = ({
  productId,
  gptGenerationTaskQueue,
  refreshPrompts,
}) => {
  const mounted = useRef(false);
  const {
    prompts: {
      selected: selectedPrompt,
      availableTags: { customerOwned },
    },
    groups: { selected: selectedGroup },
    sentences: { list: allSentences },
    productSpecificOverwrites: {
      list: allProductSpecificOverwrites,
      isLoading: productSpecificOverwriteLoading,
    },
  } = useContext(CopyAssistantContext);

  const dispatch = useContext(CopyAssistantDispatchContext);
  const { data: customer } = useGetCustomerQuery();
  const { data: structures } = useGetDocumentStructures();
  const { data: sections } = useGetDocumentSections();
  const token = useSelector((state: RootState) => state.auth.token);
  const [alteredInstruction, setAlteredInstruction] = useState<string>();
  const [alteredInputText, setAlteredInputText] = useState<string>();
  const [fieldsetText, setFieldsetText] = useState<string>();

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [enabledImageUrls, setEnabledImageUrls] = useState<string[]>([]);
  const [showImageUrlInput, setShowImageUrlInput] = useState<boolean>(false);

  const [formMode, setFormMode] = useState<PromptFormMode>(
    PromptFormMode.UPDATE
  );
  const [modalOpen, setModalOpen] = useState(false);
  const language = findLanguage(selectedPrompt, customer);
  const visible = useMemo(() => !selectedGroup, [selectedGroup]);

  const generateButtonsIsLoading = useMemo(() => {
    return allSentences.some(
      (sentence) => sentence.isLoading || sentence.isStreaming
    );
  }, [allSentences]);

  const productSpecificOverwrite = useMemo(() => {
    return allProductSpecificOverwrites.find(
      ({ prompt_id }) => prompt_id === selectedPrompt?.id
    );
  }, [allProductSpecificOverwrites, selectedPrompt]);

  // This run only once on initial render of the component
  const gptModels = useMemo(async () => {
    try {
      const models = await getModels(token);
      return models;
    } catch {
      return [];
    }
  }, [token]);

  useEffect(() => {
    let canceled = false;
    (async (): Promise<void> => {
      if (!selectedPrompt) setShowImageUrlInput(false);
      const awaitedGptModels = await gptModels;
      if (canceled) return;
      if (!awaitedGptModels.length) setShowImageUrlInput(false);
      const model = awaitedGptModels.find(
        (model) => model.model_name === selectedPrompt?.model_name
      );
      setShowImageUrlInput(model?.supports_images ?? false);
    })();

    return (): void => {
      canceled = true;
    };
  }, [selectedPrompt, gptModels]);

  useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (productSpecificOverwrite) {
      setAlteredInputText(productSpecificOverwrite?.input_data);
      const urls = productSpecificOverwrite?.image_urls || [];
      setImageUrls(urls);
      setEnabledImageUrls(urls);
    }
  }, [productSpecificOverwrite]);

  useEffect(() => {
    if (selectedPrompt) {
      setAlteredInstruction(undefined);
    }
    if (!selectedPrompt) {
      if (!alteredInstruction) {
        setAlteredInstruction(exampleInstruction);
      }
    }
  }, [selectedPrompt]);

  const { onSelect, selectionPosition, setSelectionPosition } = useSelectedText(
    alteredInstruction ?? selectedPrompt?.instruction ?? ""
  );

  const textareaRefs = useRef<HTMLTextAreaElement[]>([]);
  const instructionTextareaRef = useRef<HTMLTextAreaElement>();
  useLayoutEffect(() => {
    textareaRefs.current.forEach((textarea) => {
      preventOverflowOnTextarea(textarea);
    });
    if (selectionPosition) {
      instructionTextareaRef.current?.setSelectionRange(
        selectionPosition[0],
        selectionPosition[1]
      );
    }
  }, [selectionPosition]);

  const saveManualInputData = useCallback(
    debounce(async (inputData: string): Promise<void> => {
      await createProductSpecificOverwrite(token, {
        product_id: productId,
        input_data: inputData,
        image_urls: productSpecificOverwrite?.image_urls,
        prompt_id: selectedPrompt.id,
      }).then((overwrite) => {
        if (mounted.current) {
          setAlteredInputText(inputData);
          dispatch({
            type: CopyAssistantActionType.UPDATE_PRODUCT_SPECIFIC_OVERWRITE,
            payload: overwrite,
          });
        }
      });
    }, 500),
    [selectedPrompt, productSpecificOverwrite]
  );

  const saveManualImageUrls = useCallback(
    debounce(async (imageUrls: string[]): Promise<void> => {
      await createProductSpecificOverwrite(token, {
        product_id: productId,
        input_data: productSpecificOverwrite?.input_data,
        image_urls: imageUrls,
        prompt_id: selectedPrompt.id,
      }).then((overwrite) => {
        if (mounted.current)
          dispatch({
            type: CopyAssistantActionType.UPDATE_PRODUCT_SPECIFIC_OVERWRITE,
            payload: overwrite,
          });
      });
    }, 500),
    [selectedPrompt, productSpecificOverwrite]
  );

  const onAddSuggestion = useCallback(
    (text: string): void => {
      const instruction =
        alteredInstruction ?? selectedPrompt?.instruction ?? "";
      const setAt = selectionPosition?.[1];
      const newInstruction =
        instruction.substring(0, setAt) + text + instruction.substring(setAt);
      const newSelectionRange = setAt + text.length;
      setSelectionPosition([newSelectionRange, newSelectionRange]);
      setAlteredInstruction(newInstruction);
      document
        .getElementById("copy-assistant-template-form-instruction-input")
        ?.focus();
    },
    [
      alteredInstruction,
      selectionPosition,
      setSelectionPosition,
      selectedPrompt?.instruction,
    ]
  );

  const handleInstructionRevert = (): void => {
    setAlteredInstruction(undefined);
  };
  const handleInstructionChange = (value: string): void => {
    if (value === selectedPrompt?.instruction) {
      handleInstructionRevert();
    } else {
      setAlteredInstruction(value);
    }
  };
  const handleInputTextRevert = (): void => {
    createProductSpecificOverwrite(token, {
      product_id: productId,
      input_data: "",
      image_urls: productSpecificOverwrite?.image_urls,
      prompt_id: selectedPrompt.id,
    }).then((overwrite) => {
      if (mounted.current)
        dispatch({
          type: CopyAssistantActionType.UPDATE_PRODUCT_SPECIFIC_OVERWRITE,
          payload: overwrite,
        });
    });
    if (mounted.current) setAlteredInputText(undefined);
  };

  const handleShowForm = (mode: PromptFormMode): void => {
    setFormMode(mode);
    setModalOpen(true);
  };

  const onRefreshPrompts = async (): Promise<void> => {
    dispatch({
      type: CopyAssistantActionType.SET_PROMPT_LOADING,
      payload: true,
    });
    await refreshPrompts();
    dispatch({
      type: CopyAssistantActionType.SET_PROMPT_LOADING,
      payload: false,
    });
  };

  const disabledGenerateButton = useMemo((): boolean => {
    if (fieldsetText) {
      return false;
    }
    if (selectedPrompt) {
      if (enabledImageUrls.length > 0 && showImageUrlInput) {
        return false;
      }
      return !alteredInputText || alteredInstruction === "";
    }

    return !alteredInstruction || !alteredInputText;
  }, [
    selectedPrompt,
    fieldsetText,
    alteredInputText,
    alteredInstruction,
    enabledImageUrls,
    showImageUrlInput,
  ]);

  const setLoadingState = (loading: boolean, id?: string): void => {
    if (id) {
      dispatch({
        type: CopyAssistantActionType.SET_SPECIFIC_SENTENCE_LOADING,
        payload: { id, isLoading: loading },
      });
    }
    dispatch({
      type: CopyAssistantActionType.SET_SENTENCE_LOADING,
      payload: { loading, promptIds: [selectedPrompt?.id] },
    });
  };

  const handleGenerateAndAddToProduct = async (
    preloadedSectionInfo?: SentenceSectionInfo,
    autoTranslateAfterGeneration: boolean = false,
    mtConfigGroupId?: number
  ): Promise<void> => {
    const textToUse = fieldsetText;
    const newSentence = buildSentence(
      [],
      {
        inputText: textToUse,
        instruction: alteredInstruction,
        promptId: selectedPrompt?.id,
      },
      customer,
      structures,
      language.code
    );
    newSentence.isLoading = true;
    if (preloadedSectionInfo) {
      newSentence.sectionInfo = preloadedSectionInfo;
    }
    // Store auto-translate flags in generationInfo so they persist across page refreshes
    if (newSentence.generationInfo) {
      newSentence.generationInfo.autoTranslateAfterGeneration = autoTranslateAfterGeneration;
      newSentence.generationInfo.mtConfigGroupId = mtConfigGroupId;
    }

    dispatch({
      type: CopyAssistantActionType.ADD_SENTENCE,
      payload: newSentence,
    });

    const updateSentenceCallback = (value: string): void => {
      dispatch({
        type: CopyAssistantActionType.UPDATE_SENTENCE,
        payload: {
          id: newSentence.id,
          toUpdate: { value: { [language.code]: value }, isStreaming: true },
        },
      });
    };

    const { awaitableTimeouts, updateResult } = updateSentenceValueFactory(
      language.code,
      updateSentenceCallback
    );

    const {
      generated_texts: langStringValues,
      structured_result,
      seconds_spent_generating,
      text_block_id,
      document_section_id,
      document_section_name,
      reasoning,
      tokens_used,
      credits_used,
    } = await gptGenerationTaskQueue
      .generateOne(
        productId,
        textToUse,
        selectedPrompt?.id,
        alteredInstruction,
        enabledImageUrls,
        updateResult,
        autoTranslateAfterGeneration,
        mtConfigGroupId
      )
      .catch(
        (error): GenerateOneResponse => {
          dispatch({
            type: CopyAssistantActionType.REMOVE_SENTENCE,
            payload: newSentence.id,
          });
          const errorMessage =
            error instanceof Error ? error.message : "Generation job failed";
          store.dispatch(
            setDjangoToastOpen({
              appearance: NotificationAppearance.ERROR,
              content: `<strong>Error:</strong> ${errorMessage}`,
            })
          );
          return {
            generated_texts: [] as LangStringObject[],
            structured_result: {},
            seconds_spent_generating: 0,
          };
        }
      );

    await Promise.all(awaitableTimeouts);

    if (structured_result && Object.keys(structured_result).length > 0) {
      store.dispatch(
        setDjangoToastOpen({
          appearance: NotificationAppearance.INFO,
          content: `Metadata result:<br><pre>${JSON.stringify(
            structured_result,
            null,
            2
          )}</pre>`,
        })
      );
    }

    if (!langStringValues.length) {
      dispatch({
        type: CopyAssistantActionType.REMOVE_SENTENCE,
        payload: newSentence.id,
      });
      return;
    }

    store.dispatch(
      setDjangoToastOpen({
        appearance: NotificationAppearance.INFO,
        content: `Generation completed in ${seconds_spent_generating} seconds`,
      })
    );

    // Backend auto-saves the text block during generation and returns the text_block_id
    // in the task response (see gpt/tasks.py). Use that to build section context for the UI.
    let sectionContext: {
      templateInfo?: SentenceTemplateInfo;
      sectionInfo?: SentenceSectionInfo;
    };

    if (text_block_id && document_section_id && document_section_name) {
      // Text block was auto-saved, use the returned data
      const data: AddSentenceToProductTextBlockResponse = {
        text_block_id,
        document_section_id,
        document_section_name,
        lang_string_values: langStringValues[0],
      };
      sectionContext = getSectionContext(data, customer, structures);
    } else {
      sectionContext = {};
    }

    dispatch({
      type: CopyAssistantActionType.UPDATE_SENTENCE,
      payload: {
        id: newSentence.id,
        toUpdate: {
          ...sectionContext,
          isStreaming: false,
          isLoading: false,
          value: langStringValues[0],
          generationInfo: withGenerationLog(newSentence.generationInfo, {
            reasoning,
            tokensUsed: tokens_used,
            creditsUsed: credits_used,
          }),
        },
      },
    });
    if (sectionContext.sectionInfo) {
      dispatch({
        type: CopyAssistantActionType.ADD_ITEM_ORDER_SENTENCE_LIST,
        payload: sectionContext.sectionInfo.documentSectionName,
      });
      // Ensure preview updates immediately even when there was no prior sentence to derive channels from
      try {
        const channelIds =
          sectionContext.sectionInfo.connectedChannels?.map(({ id }) => id) ||
          [];
        if (channelIds.length) {
          reloadPreview(channelIds, language.code);
        }
      } catch {
        // ignore preview reload errors
      }
    }

    dispatch({
      type: CopyAssistantActionType.SET_SECTION_ACTIVE,
      payload: { id: newSentence.id },
    });

    if (autoTranslateAfterGeneration) {
      dispatch({
        type: CopyAssistantActionType.ADD_SENTENCE_AUTO_TRANSLATE_LIST,
        payload: {
          id: newSentence.id,
          mtConfigGroupId,
        },
      });
    }
  };
  const onGenerate = async (
    autoTranslateAfterGeneration: boolean = false,
    mtConfigGroupId?: number
  ): Promise<void> => {
    const foundSentence = allSentences.find(
      (sentence) =>
        sentence?.sectionInfo?.documentSectionId ===
        selectedPrompt?.document_section_id
    );
    setLoadingState(true, foundSentence?.id);
    let preloadSectionInfo: SentenceSectionInfo;
    if (foundSentence?.sectionInfo) {
      preloadSectionInfo = foundSentence.sectionInfo;
      dispatch({
        type: CopyAssistantActionType.ADD_ITEM_ORDER_SENTENCE_LIST,
        payload: foundSentence.sectionInfo.documentSectionName,
      });
    }
    await handleGenerateAndAddToProduct(
      preloadSectionInfo,
      autoTranslateAfterGeneration,
      mtConfigGroupId
    ).finally(() => {
      setLoadingState(false, foundSentence?.id);
      if (!autoTranslateAfterGeneration) {
        reloadPreview(
          foundSentence?.sectionInfo?.connectedChannels.map(({ id }) => id),
          selectedPrompt?.language || getCustomerEnglishLanguage(customer)?.code
        );
      }
      dispatch({
        type: CopyAssistantActionType.FETCH_NEW_PROMPT_INPUT,
        payload: true,
      });
    });
  };

  const collectFieldsetString = (str: string): void => {
    setFieldsetText(str);
  };

  const selectPrompt = (prompt: Prompt): void => {
    if (mounted.current)
      dispatch({
        type: CopyAssistantActionType.SELECT_PROMPT,
        payload: prompt,
      });
  };

  const handleUpdatePrompt = async (): Promise<void> => {
    await updatePrompt(token, {
      ...selectedPrompt,
      instruction: alteredInstruction,
    }).finally(async () => {
      await onRefreshPrompts();
    });
  };

  if (!visible) {
    return <></>;
  }
  const sectionName = sections
    ?.find((s) => s.id === selectedPrompt?.document_section_id)
    ?.name?.trim();
  return (
    <>
      <Form as="div" size="tiny">
        <Form.Field>
          {!selectedPrompt && (
            <Popup
              flowing
              size="small"
              content="Save the prompt to be able to reuse it at any time."
              trigger={
                <Button
                  data-testid={selectedPromptTestIds.buttons.saveNew}
                  color="red"
                  disabled={!alteredInstruction}
                  size="mini"
                  floated="right"
                  basic
                  compact
                  content="Save prompt"
                  onClick={(): void => handleShowForm(PromptFormMode.CREATE)}
                />
              }
            />
          )}
          {selectedPrompt?.tags.map((tag) => (
            <TemplateLabelChip
              data-testid={selectedPromptTestIds.tag(tag)}
              classNames="fitted-template-label"
              skipPredefinedClasses
              key={tag}
              content={tag}
              description={""}
              labelProps={{
                as: "span",
                size: "tiny",
              }}
            />
          ))}
          <TemplateLabelChip
            data-testid={selectedPromptTestIds.tag(
              selectedPrompt?.model_name?.trim() || "no model selected"
            )}
            classNames="!tw-p-1 tw-bg-transparent"
            skipPredefinedClasses
            key={selectedPrompt?.model_name?.trim() || "no model"}
            content={
              selectedPrompt?.model_name?.trim()
                ? "Model: " + selectedPrompt.model_name.trim()
                : "No model selected"
            }
            description={""}
            labelProps={{
              as: "span",
              size: "tiny",
            }}
          />
          <TemplateLabelChip
            data-testid={selectedPromptTestIds.tag(
              sectionName ?? "no section selected"
            )}
            classNames="!tw-p-1 tw-bg-transparent"
            skipPredefinedClasses
            key={sectionName ?? "no-section"}
            content={
              sectionName ? "Section: " + sectionName : "No section selected"
            }
            description={""}
            labelProps={{
              as: "span",
              size: "tiny",
            }}
          />
        </Form.Field>
        <Form.Field>
          <FlexDivTwoItems>
            <span>
              <Popup
                wide="very"
                header={explainInstruction.header}
                content={explainInstruction.content}
                trigger={
                  <label
                    style={{ display: "inline-block" }}
                    data-testid={selectedPromptTestIds.instruction.label}
                  >
                    <Icon name="question circle" /> Instruction:
                  </label>
                }
                size="tiny"
                hoverable
              />{" "}
              <Text
                color="grey"
                className="descriptive-helper-text"
                compact
                inline
              >
                how to write
              </Text>
            </span>
            {selectedPrompt && (
              <span>
                {alteredInstruction && selectedPrompt?.customer_id && (
                  <Popup
                    content="Save changed instruction to Prompt"
                    wide
                    size="small"
                    trigger={
                      <Button
                        content="Save changes"
                        onClick={handleUpdatePrompt}
                        size="mini"
                        basic
                        color="red"
                        compact
                      />
                    }
                  />
                )}
                {alteredInstruction !== undefined && (
                  <RevertButton
                    data-testid={selectedPromptTestIds.instruction.revert}
                    onRevert={handleInstructionRevert}
                  />
                )}
              </span>
            )}
          </FlexDivTwoItems>
          <Ref
            innerRef={(el): void => {
              textareaRefs.current.push(el);
              instructionTextareaRef.current = el;
            }}
          >
            <TextArea
              data-testid={selectedPromptTestIds.instruction.textarea}
              value={alteredInstruction ?? selectedPrompt?.instruction}
              onChange={(
                _: React.FormEvent<HTMLTextAreaElement>,
                { value }: TextAreaProps
              ): void => handleInstructionChange(value as string)}
              onSelect={onSelect}
              id="copy-assistant-template-form-instruction-input"
            />
          </Ref>
          <PromptSuggestionsComponent
            onAddSuggestion={onAddSuggestion}
            language={language?.code}
          />
        </Form.Field>
        <div>
          <Form.Field>
            <FlexDivTwoItems>
              <span>
                <Popup
                  header={explainInputText.header}
                  content={explainInputText.content}
                  trigger={
                    <label
                      style={{ display: "inline-block" }}
                      data-testid={selectedPromptTestIds.data.label}
                    >
                      <Icon name="question circle" /> Data:
                    </label>
                  }
                  size="tiny"
                  wide="very"
                  hoverable
                />{" "}
                <Text
                  color="grey"
                  className="descriptive-helper-text"
                  compact
                  inline
                >
                  what to write about
                </Text>
              </span>
              {(selectedPrompt || productSpecificOverwrite?.input_data) &&
                alteredInputText !== undefined && (
                  <RevertButton
                    onRevert={handleInputTextRevert}
                    data-testid={selectedPromptTestIds.data.revert}
                  />
                )}
            </FlexDivTwoItems>
            <FieldsetSelection
              key={selectedPrompt?.fieldset_id}
              alteredInputText={alteredInputText}
              activeFieldsetId={selectedPrompt?.fieldset_id}
              collectString={collectFieldsetString}
              saveManualInputData={saveManualInputData}
              language={selectedPrompt?.language}
            />
          </Form.Field>
          {showImageUrlInput && !productSpecificOverwriteLoading && (
            <ImageUrlInput
              value={imageUrls}
              onChange={setImageUrls}
              productId={productId}
              onSave={saveManualImageUrls}
              onEnabledChange={setEnabledImageUrls}
              skipProductImage={!selectedPrompt?.include_documents_image}
            />
          )}
          <Divider />
          <div className="tw-sticky tw-bottom-2 tw-z-[1] tw-flex tw-justify-center tw-gap-2">
            <GroupedGenerationButtons
              variant="textual-blue"
              loading={generateButtonsIsLoading}
              disabled={disabledGenerateButton}
              onGenerate={onGenerate}
              generateTestId={selectedPromptTestIds.buttons.generate}
              generateAndTranslateTestId={
                selectedPromptTestIds.buttons.generateAndTranslate
              }
            />
            <RunPipeline
              productId={productId}
              renderTrigger={(openModal): React.ReactNode => (
                <TailwindButton
                  data-testid={selectedPromptTestIds.buttons.runAgent}
                  loading={generateButtonsIsLoading}
                  variant="textual-blue"
                  content="Run agent"
                  onClick={openModal}
                />
              )}
            />
          </div>
          <GenerationDisclaimer />
        </div>
      </Form>
      <Modal open={modalOpen} onClose={(): void => setModalOpen(false)}>
        <PromptForm
          mode={formMode}
          setOpenModal={setModalOpen}
          refreshPromptList={onRefreshPrompts}
          prompt={selectedPrompt ?? { instruction: alteredInstruction }}
          availableTags={customerOwned}
          selectPrompt={selectPrompt}
        />
      </Modal>
    </>
  );
};
